import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { getLocationIntegrationStatus } from 'lib/getLocationItegrationStatus'
import { PMS_CAPABILITY_FIELDS } from 'lib/pmsCapabilities'
import type { LocationJson, LocationProductType } from 'app/types/entities'
import type { PmsLocation } from 'app/types/pms'
import { OptionType } from 'types/OptionType'

/**
 * Single source of truth for the location *integration status* heuristic across the UI.
 *
 * The "integrated" definition uses the API-owned `pmsIntegrated` boolean on each location,
 * with a fresh PMS sync determining integrated vs missingData. Every consumer that needs an
 * integrated/notIntegrated count or status dots (marketing dash badge, location dropdowns,
 * sidebar nav count, scorecards, conversions) must go through this hook so they all compute
 * the same numbers.
 *
 * The query selects exactly the fields the heuristic needs. Because every consumer issues
 * the identical query document + variables through the shared urql client, graphcache
 * dedupes them into a single cache entry — so the sidebar count and the dashboard badge
 * read from the same fetched data and never disagree.
 */

export type LocationIntegrationStatus = `integrated` | `missingData` | `notIntegrated`

export type LocationIntegrationCounts = {
  integrated: number
  missingData: number
  notIntegrated: number
  /** Locations counted as "online" for the primary badge: integrated + missingData. */
  online: number
  /** Raw total locations returned. */
  total: number
}

// Canonical field set for the integration heuristic. Keep in sync with
// getLocationIntegrationStatus / isLocationIntegrated. `products`, the live flags and the
// history stamps are here so callers can narrow the list to locations where a product is in
// use; they stay in the single shared document so every consumer keeps deduping into one
// graphcache entry.
const LOCATION_STATUS_FIELDS = `id name lastPmsSyncDate products isAIAgentLive isSchedulerLive lastAICallDate ${PMS_CAPABILITY_FIELDS}`

// `products` records what a location is entitled to, which ops sets when the product is
// sold. These flags are the separate per-location switch that says it is actually running.
// Only these two products have one.
const PRODUCT_LIVE_FLAGS: Record<string, 'isAIAgentLive' | 'isSchedulerLive'> = {
  'AI Agent': 'isAIAgentLive',
  Scheduler: 'isSchedulerLive',
}

// The date of the last call the AI agent took at a location (TRK-5518). A location that ran
// the agent keeps its history on the product dashboard after the switch goes off, so this
// stands in for "the product was in use here". Only AI Agent records such a stamp.
const PRODUCT_HISTORY_FIELDS: Record<string, 'lastAICallDate'> = {
  'AI Agent': 'lastAICallDate',
}

/**
 * Whether a location belongs on a product's dashboard. Without `requireInUse` this is pure
 * entitlement, which ops sets when the product is sold. With it, the location must also be
 * switched on — unless it already produced data for that product, in which case its history
 * keeps it selectable so that data stays reachable.
 */
function isRelevantToProduct(location: LocationJson, product: LocationProductType, requireInUse: boolean): boolean {
  const historyField = PRODUCT_HISTORY_FIELDS[product]
  if (requireInUse && historyField && location[historyField]) return true
  if (!(location.products ?? []).includes(product)) return false
  if (!requireInUse) return true
  const liveFlag = PRODUCT_LIVE_FLAGS[product]
  return !liveFlag || !!location[liveFlag]
}

/**
 * The one place the frontend puts a problem into words. Dropdown rows, dot tooltips and the
 * coverage notice all read from here, so a location is described the same way everywhere
 * rather than being an "Integration Error" in one surface and "stale" in another.
 */
function toStatusLabel(status: LocationIntegrationStatus, lastPmsSyncDate?: string): string | undefined {
  if (status === `integrated`) return undefined
  if (status === `notIntegrated`) return `Not connected`
  if (!lastPmsSyncDate) return `Sync stalled`
  const synced = moment(lastPmsSyncDate)
  return `Synced ${synced.isSame(moment(), `year`) ? synced.format(`MMM D`) : synced.format(`MMM D, YYYY`)}`
}

function toOption(location: LocationJson): OptionType {
  const integrationStatus = getLocationIntegrationStatus(location)
  return {
    id: location.id!,
    value: location.id!,
    name: location.name!,
    meta: {
      integrationStatus,
      // Carried so consumers can name the problem, not just count it: a stale location
      // needs its last sync date to explain itself.
      lastPmsSyncDate: location.lastPmsSyncDate,
      statusLabel: toStatusLabel(integrationStatus, location.lastPmsSyncDate),
      // What lib/pmsCapabilities reads, so a picker can offer only the locations that serve its feature.
      pms: { pmsIntegrated: location.pmsIntegrated, kollaConsumerId: location.kollaConsumerId } satisfies PmsLocation,
    },
  }
}

function tallyCounts(options: OptionType[]): LocationIntegrationCounts {
  const counts = { integrated: 0, missingData: 0, notIntegrated: 0 }
  for (const option of options) {
    const status = option.meta?.integrationStatus as LocationIntegrationStatus | undefined
    if (status === `integrated`) counts.integrated++
    else if (status === `missingData`) counts.missingData++
    else if (status === `notIntegrated`) counts.notIntegrated++
  }
  return {
    ...counts,
    online: counts.integrated + counts.missingData,
    total: options.length,
  }
}

/**
 * Fetches locations and computes integration status once. Consumers read the option list
 * (with `meta.integrationStatus` for dropdown dots) and/or the derived counts.
 *
 * @param deps optional dependency list to re-fetch on (e.g. selected location ids)
 * @param locationProduct when set, only locations entitled to this product are returned.
 *   Filtering happens client-side so the shared query document stays identical across consumers.
 * @param requireProductInUse additionally require that the product is in use at the location:
 *   switched on now, or already responsible for data there. Only meaningful alongside
 *   `locationProduct`. See isRelevantToProduct.
 */
export function useLocationIntegrationStatus(
  deps: unknown[] = [],
  locationProduct?: LocationProductType | LocationProductType[],
  requireProductInUse = false,
): {
  locationOptions: OptionType[]
  counts: LocationIntegrationCounts
  isLoading: boolean
  refresh: () => void
} {
  const [locationOptions, setLocationOptions] = useState<OptionType[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const fetchLocations = () => {
    setIsLoading(true)
    gqlFetchList<LocationJson>(`locations`, {}, LOCATION_STATUS_FIELDS)
      .then((data) => {
        const hits = data.hits ?? []
        const wanted = locationProduct ? (Array.isArray(locationProduct) ? locationProduct : [locationProduct]) : []
        const scoped = wanted.length
          ? hits.filter((location) =>
              wanted.some((product) => isRelevantToProduct(location, product, requireProductInUse)),
            )
          : hits
        setLocationOptions(scoped.map(toOption))
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }

  /**
   * The products as a string, not as whatever array the caller built.
   *
   * `locationProduct` accepts an array, and an array literal is a new identity
   * on every render, so a caller writing the natural thing
   * (`useLocationIntegrationStatus([], ['Scheduler', 'AI Agent'])`) refired this
   * effect every render: fetch, setState, render, fetch. It locked the tab hard
   * enough for Chrome to offer to kill the page. Keying on the value rather
   * than the reference makes the signature safe to use as it reads.
   */
  const productKey = Array.isArray(locationProduct) ? locationProduct.join(`,`) : locationProduct

  useEffect(() => {
    fetchLocations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, productKey, requireProductInUse])

  const counts = useMemo(() => tallyCounts(locationOptions), [locationOptions])

  return { locationOptions, counts, isLoading, refresh: fetchLocations }
}
